// src/features/deliverySlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import deliveryAxios from "../utils/deliveryAxios";

const TOKEN_KEY = "deliveryAuthToken";
const DELIVERY_PARTNER_DATA_KEY = "deliveryPartnerData";

// Try to load persisted data
const persistedToken = localStorage.getItem(TOKEN_KEY);
const persistedDeliveryPartner = (() => {
  try {
    const s = localStorage.getItem(DELIVERY_PARTNER_DATA_KEY);
    return s ? JSON.parse(s) : null;
  } catch (e) {
    return null;
  }
})();

// Async thunks
export const loginDeliveryPartner = createAsyncThunk(
  "delivery/login",
  async ({ phone, password }, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.post("/login", { phone, password });
      const { token, deliveryBoy } = response.data.responseData;
      
      // Persist to localStorage
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(DELIVERY_PARTNER_DATA_KEY, JSON.stringify(deliveryBoy));
      
      return { token, deliveryPartner: deliveryBoy };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Login failed"
      );
    }
  }
);

export const fetchAssignedOrders = createAsyncThunk(
  "delivery/fetchOrders",
  async ({ page = 0, size = 20, search = "", status = "", startDate = "", endDate = "" }, { rejectWithValue }) => {
    try {
      const params = { page, size };
      if (search) params.search = search;
      if (status) params.status = status;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await deliveryAxios.get("/orders", { params });
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to fetch orders"
      );
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  "delivery/updateStatus",
  async ({ orderId, status }, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.put(`/orders/${orderId}/status`, { status });
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to update status"
      );
    }
  }
);

export const schedulePickup = createAsyncThunk(
  "delivery/schedulePickup",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.post(`/orders/${orderId}/schedule-pickup`);
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to schedule pickup"
      );
    }
  }
);

export const markReturnInTransit = createAsyncThunk(
  "delivery/markInTransit",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.post(`/orders/${orderId}/mark-in-transit`);
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to mark as in transit"
      );
    }
  }
);

export const markBookReturned = createAsyncThunk(
  "delivery/markReturned",
  async ({ orderId, collectedBy }, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.post(`/orders/${orderId}/mark-returned`, { collectedBy });
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to mark as returned"
      );
    }
  }
);

export const verifyOtpAndDeliver = createAsyncThunk(
  "delivery/verifyOtp",
  async ({ orderId, otp }, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.post(`/orders/${orderId}/verify-delivery`, { otp });
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to verify OTP"
      );
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  "delivery/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.get("/stats");
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to fetch stats"
      );
    }
  }
);

export const fetchDeliveryPartnerProfile = createAsyncThunk(
  "delivery/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await deliveryAxios.get("/profile");
      return response.data.responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.responseMessage || "Failed to fetch profile"
      );
    }
  }
);

const deliverySlice = createSlice({
  name: "delivery",
  initialState: {
    token: persistedToken || null,
    deliveryPartner: persistedDeliveryPartner || null,
    isAuthenticated: !!persistedToken,
    orders: {
      content: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: 0,
      size: 20,
      loading: false,
      error: null,
    },
    stats: {
      totalOrders: 0,
      processingOrders: 0,
      shippedOrders: 0,
      onTheWayOrders: 0,
      deliveredOrders: 0,
      completedToday: 0,
      pendingDeliveries: 0,
      loading: false,
      error: null,
    },
    loginLoading: false,
    loginError: null,
  },
  reducers: {
    logout: (state) => {
      state.token = null;
      state.deliveryPartner = null;
      state.isAuthenticated = false;
      state.orders = {
        content: [],
        totalElements: 0,
        totalPages: 0,
        currentPage: 0,
        size: 20,
        loading: false,
        error: null,
      };
      state.stats = {
        totalOrders: 0,
        processingOrders: 0,
        shippedOrders: 0,
        onTheWayOrders: 0,
        deliveredOrders: 0,
        completedToday: 0,
        pendingDeliveries: 0,
        loading: false,
        error: null,
      };
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(DELIVERY_PARTNER_DATA_KEY);
    },
    clearOrders: (state) => {
      state.orders.content = [];
      state.orders.totalElements = 0;
      state.orders.totalPages = 0;
      state.orders.currentPage = 0;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginDeliveryPartner.pending, (state) => {
        state.loginLoading = true;
        state.loginError = null;
      })
      .addCase(loginDeliveryPartner.fulfilled, (state, action) => {
        state.loginLoading = false;
        state.token = action.payload.token;
        state.deliveryPartner = action.payload.deliveryPartner;
        state.isAuthenticated = true;
      })
      .addCase(loginDeliveryPartner.rejected, (state, action) => {
        state.loginLoading = false;
        state.loginError = action.payload;
      });

    // Fetch Orders
    builder
      .addCase(fetchAssignedOrders.pending, (state) => {
        state.orders.loading = true;
        state.orders.error = null;
      })
      .addCase(fetchAssignedOrders.fulfilled, (state, action) => {
        state.orders.loading = false;
        if (action.payload.number === 0) {
          state.orders.content = action.payload.content || [];
        } else {
          const existingIds = new Set((state.orders.content || []).map((o) => o.orderId));
          const newOrders = (action.payload.content || []).filter((o) => !existingIds.has(o.orderId));
          state.orders.content = [...state.orders.content, ...newOrders];
        }
        state.orders.totalElements = action.payload.totalElements;
        state.orders.totalPages = action.payload.totalPages;
        state.orders.currentPage = action.payload.number;
        state.orders.size = action.payload.size;
      })
      .addCase(fetchAssignedOrders.rejected, (state, action) => {
        state.orders.loading = false;
        state.orders.error = action.payload;
      });

    // Update Status
    builder
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        // Update the order in the list
        const index = state.orders.content.findIndex(
          (order) => order.orderId === action.payload.orderId
        );
        if (index !== -1) {
          state.orders.content[index] = action.payload;
        }
      });

    // Schedule Pickup
    builder
      .addCase(schedulePickup.fulfilled, (state, action) => {
        const index = state.orders.content.findIndex(
          (order) => order.orderId === action.payload.orderId
        );
        if (index !== -1) {
          state.orders.content[index] = action.payload;
        }
      });

    // Mark Return In Transit
    builder
      .addCase(markReturnInTransit.fulfilled, (state, action) => {
        const index = state.orders.content.findIndex(
          (order) => order.orderId === action.payload.orderId
        );
        if (index !== -1) {
          state.orders.content[index] = action.payload;
        }
      });

    // Mark Book Returned
    builder
      .addCase(markBookReturned.fulfilled, (state, action) => {
        const index = state.orders.content.findIndex(
          (order) => order.orderId === action.payload.orderId
        );
        if (index !== -1) {
          state.orders.content[index] = action.payload;
        }
      });

    // Verify OTP and Deliver
    builder
      .addCase(verifyOtpAndDeliver.fulfilled, (state, action) => {
        const index = state.orders.content.findIndex(
          (order) => order.orderId === action.payload.orderId
        );
        if (index !== -1) {
          state.orders.content[index] = action.payload;
        }
      });

    // Fetch Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.stats.loading = true;
        state.stats.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.stats.loading = false;
        state.stats = { ...state.stats, ...action.payload, loading: false };
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.stats.loading = false;
        state.stats.error = action.payload;
      });

    // Fetch Profile
    builder
      .addCase(fetchDeliveryPartnerProfile.fulfilled, (state, action) => {
        state.deliveryPartner = action.payload;
        localStorage.setItem(DELIVERY_PARTNER_DATA_KEY, JSON.stringify(action.payload));
      });
  },
});

export const { logout, clearOrders } = deliverySlice.actions;
export default deliverySlice.reducer;
